if (!customElements.get('product-charm-picker')) {
  customElements.define(
    'product-charm-picker',
    class ProductCharmPicker extends HTMLElement {
      connectedCallback() {
        this.sectionRoot = this.closest('[id^="shopify-section-"]');
        this.sectionId = this.dataset.sectionId || '';
        this.storageKey = this.sectionId ? `product-charm-picker:${this.sectionId}` : '';
        this.maxSelections = parseInt(this.dataset.maxSelections, 10);
        if (Number.isNaN(this.maxSelections) || this.maxSelections < 0) this.maxSelections = 2;
        this.cartAddUrl =
          (typeof window.routes !== 'undefined' && window.routes.cart_add_url) ||
          this.dataset.cartAddUrl ||
          '/cart/add.js';
        this.selected = new Set();
        this.countEl = this.querySelector('.product-charm-picker__count-text');
        this.tiles = this.querySelectorAll('.product-charm-picker__tile');
        this.formAttachAttempts = 0;

        this.tiles.forEach((tile) => tile.addEventListener('click', () => this.onTileClick(tile)));

        this.restoreSelectionFromStorage();
        this.updateCountLabel();

        this.boundSubmit = this.onFormSubmitCapture.bind(this);
        this.boundClickCapture = this.onAddButtonClickCapture.bind(this);
        this.attachToProductForm();
      }

      restoreSelectionFromStorage() {
        if (!this.storageKey) return;
        try {
          const raw = sessionStorage.getItem(this.storageKey);
          if (!raw) return;
          const indices = JSON.parse(raw);
          if (!Array.isArray(indices)) return;
          indices.forEach((idx) => {
            const key = String(idx);
            if (this.selected.size >= this.maxSelections) return;
            this.selected.add(key);
            const tile = this.querySelector(`[data-tile-index="${key}"]`);
            if (tile) {
              tile.classList.add('is-selected');
              tile.setAttribute('aria-pressed', 'true');
            }
          });
        } catch (e) {
          /* ignore */
        }
      }

      persistSelection() {
        if (!this.storageKey) return;
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify([...this.selected]));
        } catch (e) {
          /* ignore */
        }
      }

      attachToProductForm() {
        const formId = this.sectionId ? `product-form-${this.sectionId}` : '';
        this.form = formId
          ? this.sectionRoot?.querySelector(`form#${CSS.escape(formId)}`)
          : this.sectionRoot?.querySelector('product-form.product-form form');
        this.productFormEl = this.form?.closest('product-form');

        if (this.form && this.productFormEl) {
          if (!this.formListenerAttached) {
            this.form.addEventListener('submit', this.boundSubmit, true);
            this.productFormEl.addEventListener('click', this.boundClickCapture, true);
            this.formListenerAttached = true;
          }
          return;
        }

        this.formAttachAttempts += 1;
        if (this.formAttachAttempts < 100) {
          setTimeout(() => this.attachToProductForm(), 50);
        }
      }

      disconnectedCallback() {
        if (this.form && this.boundSubmit && this.formListenerAttached) {
          this.form.removeEventListener('submit', this.boundSubmit, true);
        }
        if (this.productFormEl && this.boundClickCapture && this.formListenerAttached) {
          this.productFormEl.removeEventListener('click', this.boundClickCapture, true);
        }
        this.formListenerAttached = false;
      }

      updateCountLabel() {
        if (this.countEl) {
          this.countEl.textContent = `${this.selected.size} / ${this.maxSelections} selected`;
        }
      }

      onTileClick(tile) {
        if (tile.disabled) return;
        const idx = tile.dataset.tileIndex;
        if (idx === undefined) return;
        if (this.selected.has(idx)) {
          this.selected.delete(idx);
          tile.classList.remove('is-selected');
          tile.setAttribute('aria-pressed', 'false');
        } else if (this.maxSelections === 0) {
          return;
        } else if (this.selected.size < this.maxSelections) {
          this.selected.add(idx);
          tile.classList.add('is-selected');
          tile.setAttribute('aria-pressed', 'true');
        }
        this.persistSelection();
        this.updateCountLabel();
      }

      getSelectedCharmLineItems() {
        const items = [];
        for (const idx of this.selected) {
          const tile = this.querySelector(`.product-charm-picker__tile[data-tile-index="${idx}"]`);
          if (!tile || tile.disabled) continue;
          const vid = tile.dataset.charmVariantId;
          if (!vid) continue;
          const id = Number(vid);
          if (id) items.push({ id, quantity: 1 });
        }
        return items;
      }

      appendSectionsToFormData(formData) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cart && typeof cart.getSectionsToRender === 'function') {
          const ids = cart.getSectionsToRender().map((s) => s.id);
          formData.append('sections', ids.join(','));
          formData.append('sections_url', window.location.pathname);
        }
      }

      sectionsPayload() {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (!cart || typeof cart.getSectionsToRender !== 'function') return {};
        const ids = cart.getSectionsToRender().map((s) => s.id);
        return {
          sections: ids.join(','),
          sections_url: window.location.pathname,
        };
      }

      setButtonLoading(productFormEl, loading) {
        const submitButton = productFormEl.querySelector('[type="submit"][name="add"]');
        if (!submitButton) return;
        const spinner = submitButton.querySelector('.loading__spinner');
        if (loading) {
          submitButton.setAttribute('aria-disabled', true);
          submitButton.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        } else {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      tryCharmAdd(event) {
        if (!this.form || !this.productFormEl) return false;

        const charmItems = this.getSelectedCharmLineItems();
        if (charmItems.length === 0) return false;

        const submitButton = this.productFormEl.querySelector('[type="submit"][name="add"]');
        if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return false;
        if (submitButton && submitButton.disabled) return false;

        const idInput = this.form.querySelector('[name="id"]');
        if (!idInput || idInput.disabled) return false;

        const mainVariantId = idInput.value;

        const mainId = Number(mainVariantId);
        if (!mainVariantId || !mainId) return false;

        if (event) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const productFormEl = this.productFormEl;

        this.setButtonLoading(productFormEl, true);

        const formData = new FormData(this.form);
        this.appendSectionsToFormData(formData);
        if (cart && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }

        const fdConfig = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        };

        fetch(this.cartAddUrl, fdConfig)
          .then((r) => r.json())
          .then((mainResponse) => {
            if (mainResponse.status) {
              this.showError(productFormEl, mainResponse);
              return null;
            }

            const charmBody = {
              items: charmItems,
              ...this.sectionsPayload(),
            };

            return fetch(this.cartAddUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify(charmBody),
            }).then((r) => r.json());
          })
          .then((charmResponse) => {
            if (charmResponse == null) return;
            if (charmResponse.status) {
              this.showError(productFormEl, charmResponse);
              return;
            }

            if (!cart) {
              window.location = window.routes?.cart_url || '/cart';
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-charm-picker',
              productVariantId: String(mainVariantId),
              cartData: charmResponse,
            });

            cart.setActiveElement(document.activeElement);
            cart.renderContents(charmResponse);
            if (cart.classList && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.setButtonLoading(productFormEl, false);
          });

        return true;
      }

      showError(productFormEl, response) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-charm-picker',
          errors: response.errors || response.description,
          message: response.message,
        });
        const msg =
          response.description || response.message || response.errors || 'Could not add to cart';
        const wrap = productFormEl.querySelector('.product-form__error-message-wrapper');
        const err = productFormEl.querySelector('.product-form__error-message');
        if (wrap && err) {
          err.textContent = typeof msg === 'string' ? msg : JSON.stringify(msg);
          wrap.hidden = false;
        }
      }

      onFormSubmitCapture(event) {
        if (this.tryCharmAdd(event)) return;
      }

      onAddButtonClickCapture(event) {
        const btn = event.target.closest?.('[type="submit"][name="add"]');
        if (!btn || !this.productFormEl || !this.productFormEl.contains(btn)) return;
        if (this.tryCharmAdd(event)) return;
      }
    }
  );
}
